import { toSnakeCase, toCamelCase } from "./string_helpers";

function ident(e) {
  return e;
}

function mapEntries(fk) {
  return (fv) => (obj) =>
    Object.entries(obj).reduce((o, [k, v]) => ({ ...o, [fk(k)]: fv(v) }), {});
}

function mapEntriesRecursive(fk) {
  return (fv) =>
    function g(obj) {
      return obj == null || typeof obj !== "object"
        ? obj
        : Array.isArray(obj)
          ? obj.map(g)
          : mapEntries(fk)((...v) => g(fv(...v)))(obj);
    };
}

export function interpolator(values, low, high) {
  const minValue = Math.min(...values);
  const maxValue = Math.max(...values);
  const range = maxValue - minValue;
  const scale = (high - low) / range;

  return function (value) {
    return low + (value - minValue) * scale;
  };
}

export function camelizeObjKeys(obj) {
  return mapEntriesRecursive(toCamelCase)(ident)(obj);
}

export function snakeCaseObjKeys(obj) {
  return mapEntriesRecursive(toSnakeCase)(ident)(obj);
}

// Remove item from collection, returning found item and resulting collection
export function extractFirst(vs, f) {
  let len = vs.length;
  for (var i = 0; i < len; i++) {
    if (f(vs[i])) {
      return [vs[i], [...vs.slice(0, i), ...vs.slice(i + 1)]];
    }
  }

  return [undefined, vs];
}

// Find item from collection based on set of predicate functions. For each
// function, traverse the whole set and only if nothing matches, then move on
// to the next function.
//
// Returns [match, vs (without match)]
//
export function extractCascade(vs, ...f) {
  let len = f.length;
  for (var i = 0; i < len; i++) {
    const res = extractFirst(vs, f[i]);
    if (res[0] !== undefined) return res;
  }

  return [undefined, vs];
}

export function partition(vs, f, fa = (a) => a) {
  let a = [],
    b = [];

  let len = vs.length;
  for (var x = 0; x < len; x++) {
    if (f(vs[x])) {
      a.push(fa(vs[x]));
    } else {
      b.push(fa(vs[x]));
    }
  }

  return [a, b];
}

export function isMonotonic(vs, f = (x) => x) {
  let asc = null;
  for (var i = 0, len = vs.length - 1; i < len; i++) {
    if (f(vs[i]) > f(vs[i + 1])) {
      if (asc === null) asc = false;
      if (asc === true) return false;
    } else if (f(vs[i]) < f(vs[i + 1])) {
      if (asc === null) asc = true;
      if (asc === false) return false;
    }
  }
  return true;
}

export function ensureResultsMonotonic(vs, field) {
  if (!isMonotonic(vs, (x) => x.parsedField(field))) {
    return vs
      .slice()
      .sort((a, b) => a.parsedField(field) - b.parsedField(field));
  } else {
    return vs;
  }
}

export function groupBy(xs, f = (x) => x) {
  return xs.reduce((rv, x) => {
    const k = f(x);
    (rv[k] = rv[k] || []).push(x);
    return rv;
  }, {});
}

// Group by, but only store a single item per group
//
export function groupReduce(xs, f = (x) => x) {
  return xs.reduce((rv, x) => {
    const k = f(x);
    rv[k] = x;
    return rv;
  }, {});
}

export function nestedGroupByToMap(
  xs,
  keyFn = (x) => x,
  translateFn = (x) => x,
) {
  return xs.reduce((rv, x) => {
    const [k1, k2] = keyFn(x);
    rv.set(k1, (rv.get(k1) || new Map()).set(k2, translateFn(x)));
    return rv;
  }, new Map());
}

// Convert nested Map to array of objects, with root nesting key folded into result set
export function foldedMap(nestedMap, key) {
  return Array.from(nestedMap.entries(), ([x, v]) =>
    Object.fromEntries(v.set(key, x).entries()),
  );
}

export function nestedGroupBy(xs, keyFn = (x) => x, translateFn = (x) => x) {
  return xs.reduce((rv, x) => {
    const [k1, k2] = keyFn(x);
    (rv[k1] ||= {})[k2] = translateFn(x);
    return rv;
  }, {});
}

export function nestedMapGet(m, vs) {
  let nextM = m;
  vs.forEach((v) => {
    nextM = nextM.get(v);
    if (nextM === undefined) return undefined;
  });
  return nextM;
}

export function mapNestedGroupBy(xs, f = (x) => x) {
  let m = new Map();
  xs.forEach((x) => {
    const [k1, k2] = f(x);
    let innerMap = m.get(k1) || new Map();
    innerMap.set(k2, x);
    m.set(k1, innerMap);
  });
  return m;
}

export function incrementedIds(length, fn = (i) => i) {
  return Array.from({ length }, (_, i) => fn(i));
}

export function without(obj, ...values) {
  const s = new Set(values);
  return Object.fromEntries(Object.entries(obj).filter(([, v]) => !s.has(v)));
}
